import React, {
  memo,
  useMemo,
  useState,
} from 'react';

import {
  Avatar,
  Badge,
  Button,
  message,
  Typography,
} from 'antd';
import moment from 'moment';
import {
  IoBan,
  IoCalendarOutline,
  IoCheckmark,
  IoPricetags,
} from 'react-icons/io5';
import { BottomSheet } from 'react-spring-bottom-sheet';

import UserAPI from '../../../api/UserAPI';
import BottomSheetTile
  from '../../../components/BottomSheetTile/BottomSheetTile';
import CustomReadMoreContent
  from '../../../components/CustomReadMoreText/CustomReadMoreContent';
import MenuButton from '../../../components/MenuButton/MenuButton';
import ConfirmationModal
  from '../../../components/Modals/ConfirmationModal/ConfirmationModal';
import SocialMediaLink
  from '../../../components/SocialMediaLink/SocialMediaLink';
import { useTheme } from '../../../context/ThemeProvider';
import {
  setServiceRequestModalState,
} from '../../../context/tqConnectServiceRequestReducer';
import showAppError from '../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../shared/hooks';
import { nFormat } from '../../../shared/utils';
import { IPointsConfig } from '../../../types/HostMetadata';
import { ROUTES } from '../../../types/routes';
import { Onboarding } from '../../../types/userTypes';
import ChatAvatar from '../../Messages/components/ChatAvatar/ChatAvatar';
import PointsPill
  from '../../Profile/screens/Leaderboard/components/PointsPill/PointsPill';
import {
  IUserProfileDetails,
  UserProfileView,
  UserProfileViewType,
} from '../types';
import UserTQConnectStarCard from './UserTQConnectStats/UserTQConnectStarCard';

interface Props {
  profileDetails: IUserProfileDetails;
  setProfileDetails: (details: IUserProfileDetails) => void;
  pointsConfig: IPointsConfig;
  isTagMango: boolean;
  mangoesTitle: string;
  userId: string;
  showAboutContent?: boolean;
  canRequestForService?: boolean;
  view: UserProfileViewType;
}

const UserProfileCard: React.FC<Props> = ({
  profileDetails,
  setProfileDetails,
  pointsConfig,
  isTagMango,
  mangoesTitle,
  userId,
  showAboutContent = false,
  canRequestForService = false,
  view,
}) => {
  const dispatch = useAppDispatch();
  const { colors } = useTheme();
  const navigate = useAppNavigate();
  const userDetails = useAppSelector((state) => state.user);

  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false);
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);

  // const { pointsImage, pointsName } = pointsConfig;

  const pointsImage = pointsConfig?.pointsImage;
  const pointsName = pointsConfig?.pointsName;

  const actionPermissions = useMemo(() => {
    const canEditProfile = profileDetails?.isViewerSelf;
    const canChat =
      profileDetails?.allowChat &&
      !profileDetails?.isViewerSelf &&
      userDetails.type !== Onboarding.CREATOR_RESTRICTED;
    const canViewStorefront =
      profileDetails?.accountType === 'creator' &&
      profileDetails?.storefrontLink;
    const canBlockOrUnblock =
      !isTagMango &&
      profileDetails?.accountType !== 'creator' &&
      profileDetails?.viewerType === 'creator';

    return {
      canViewMangoesPurchased:
        profileDetails?.viewerType === 'creator' &&
        profileDetails?.accountType === 'subscriber' &&
        !isTagMango,
      canEditProfile,
      canChat,
      canViewStorefront,
      canBlockOrUnblock,
      canRequestForService,
    };
  }, [
    canRequestForService,
    isTagMango,
    profileDetails?.accountType,
    profileDetails?.allowChat,
    profileDetails?.isViewerSelf,
    profileDetails?.storefrontLink,
    profileDetails?.viewerType,
    userDetails.type,
  ]);

  const state = useMemo(() => {
    const {
      accountType,
      userProfile: {
        about,
        styledAbout,
        score,
        badges,
        facebook,
        linkedin,
        instagram,
        twitter,
        youtube,
        isBlockedFromCommunityEngagement,
      },
    } = profileDetails;

    return {
      isCreatorAccount: accountType === 'creator',
      isSubscriberAccount: accountType === 'subscriber',
      aboutContent: styledAbout ?? about,
      isGamifiedSubscriber:
        accountType !== 'creator' && typeof score === 'number' && !isTagMango,
      isPointsAvailable: pointsConfig && typeof score === 'number',
      isBadgesAvailable: badges && Array.isArray(badges) && badges.length > 0,
      hasSocialLinks: facebook || linkedin || instagram || twitter || youtube,
      isBlocked: profileDetails ? isBlockedFromCommunityEngagement : false,
    };
  }, [profileDetails, isTagMango, pointsConfig]);

  const showConfirmationDialog = () => {
    setIsDialogVisible(true);
  };

  const hideConfirmationDialog = () => {
    setIsDialogVisible(false);
  };

  const showBottomSheet = () => {
    setIsBottomSheetVisible(true);
  };

  const hideBottomSheet = () => {
    setIsBottomSheetVisible(false);
  };

  const handleBlockSubscriber = async () => {
    setUpdating(true);
    try {
      const res = await UserAPI.blockUser(userId, !state.isBlocked);
      if (res.status === 200) {
        message.success(
          !state.isBlocked
            ? 'User blocked successfully!'
            : 'User unblocked successfully!',
        );
        setProfileDetails({
          ...profileDetails,
          userProfile: {
            ...profileDetails.userProfile,
            isBlockedFromCommunityEngagement: !state.isBlocked,
          },
        });
        hideConfirmationDialog();
      } else {
        showAppError(res.data);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setUpdating(false);
    }
  };

  const ProfileDetailsComponent = useMemo(() => {
    const { viewerType, isViewerSelf, userProfile } = profileDetails;
    const {
      profilePicUrl,
      name,
      facebook,
      linkedin,
      instagram,
      twitter,
      youtube,
      createdAt,
      score,
      leaderboardRank,
      badges,
      isBlockedFromCommunityEngagement,
    } = userProfile;

    // if showAboutContent is true, and any of the following are not present:
    // - state.aboutContent (the about content)
    // - createdAt (the date the user joined)
    // - isBlockedFromCommunityEngagement (whether the user is blocked from community engagement)
    // - state.hasSocialLinks (whether the user has social links)
    // show empty message as there is no content to show
    if (
      showAboutContent &&
      !(
        state.aboutContent ||
        createdAt ||
        isBlockedFromCommunityEngagement ||
        state.hasSocialLinks
      )
    ) {
      return (
        <div className="profile-card">
          <Typography.Text>
            {profileDetails.userProfile.name
              ? `${profileDetails.userProfile.name} has not added anything yet.`
              : 'Nothing to show here.'}
          </Typography.Text>
        </div>
      );
    }

    return (
      <div className="profile-card">
        {showAboutContent ? (
          <>
            {state.aboutContent ? (
              <CustomReadMoreContent
                formatType="parsed"
                className="bio-description">
                {state.aboutContent}
              </CustomReadMoreContent>
            ) : null}

            {createdAt ? (
              <div className="join-date">
                <IoCalendarOutline size={14} />
                Joined on {moment(createdAt).format('MMM DD, YYYY')}
              </div>
            ) : null}

            {isBlockedFromCommunityEngagement ? (
              <div className="blocked-message">
                <IoBan size={20} />
                Profile has been blocked by the creator from posting or
                commenting on the Feed Page.
              </div>
            ) : null}

            {state.hasSocialLinks ? (
              <div className="socials-wrapper">
                {facebook ? (
                  <SocialMediaLink type="facebook" url={facebook} />
                ) : null}
                {linkedin ? (
                  <SocialMediaLink type="linkedin" url={linkedin ?? ''} />
                ) : null}
                {instagram ? (
                  <SocialMediaLink type="instagram" url={instagram ?? ''} />
                ) : null}
                {twitter ? (
                  <SocialMediaLink type="twitter" url={twitter ?? ''} />
                ) : null}
                {youtube ? (
                  <SocialMediaLink type="youtube" url={youtube ?? ''} />
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="name-wrapper">
              <Badge
                count={
                  state.isGamifiedSubscriber
                    ? nFormat(leaderboardRank ?? 0, 999, 2)
                    : 0
                }
                overflowCount={99}
                title={`Your rank - ${
                  state.isGamifiedSubscriber
                    ? nFormat(leaderboardRank ?? 0, 999, 2)
                    : 0
                }`}
                offset={[-5, 50]}
                color={colors.PRIMARY}>
                <ChatAvatar
                  size={65}
                  url={profilePicUrl}
                  name={name}
                  border={1}
                  selected
                />
              </Badge>

              <div className="name-wrapper__content">
                <Typography.Title level={4} className="title">
                  {name}
                </Typography.Title>

                {state.isCreatorAccount ? (
                  <Typography.Text className="creator-tag">
                    CREATOR
                  </Typography.Text>
                ) : null}
                {state.isGamifiedSubscriber ? (
                  <div className="gamification-details">
                    {score ? (
                      <PointsPill
                        points={score ?? 0}
                        icon={pointsImage}
                        name={pointsName}
                        iconSize={20}
                      />
                    ) : null}

                    {state.isBadgesAvailable ? (
                      <Avatar.Group maxCount={3}>
                        {badges?.map((elem) => (
                          <Avatar
                            key={elem._id}
                            src={elem.imageUrl}
                            size={32}
                          />
                        ))}
                      </Avatar.Group>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="action-wrapper">
              {actionPermissions?.canViewStorefront ? (
                <>
                  <Button
                    className="siteBtn grayBtn primaryBtn"
                    onClick={() => navigate(ROUTES.STOREFRONT)}>
                    View Storefront
                  </Button>
                </>
              ) : null}
              {actionPermissions?.canChat ? (
                <>
                  <Button
                    className="siteBtn grayBtn primaryBtn"
                    onClick={() =>
                      navigate(ROUTES.MESSAGE_DM_PARTICIPANT, {
                        otherParticipantId: userId,
                      })
                    }>
                    Chat
                  </Button>
                </>
              ) : null}
              {actionPermissions?.canEditProfile ? (
                <>
                  <Button
                    className="siteBtn grayBtn"
                    onClick={() => navigate(ROUTES.PERSONAL_INFO)}>
                    Edit Profile
                  </Button>
                </>
              ) : null}

              {Object.values(actionPermissions).filter(Boolean).length > 1 ? (
                <>
                  <MenuButton
                    className="siteBtn grayBtn menuBtn"
                    onClick={showBottomSheet}
                  />
                </>
              ) : null}
            </div>
            {state.isSubscriberAccount &&
            view === UserProfileView.TQ_CONNECT_PROFILE &&
            !isTagMango ? (
              <>
                <UserTQConnectStarCard
                  userId={userId}
                  userType={profileDetails.userProfile.onboarding}
                  viewerType={viewerType}
                  isViewerSelf={isViewerSelf}
                />
              </>
            ) : null}
          </>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileDetails,
    state.isGamifiedSubscriber,
    state.isCreatorAccount,
    state.isBadgesAvailable,
    state.aboutContent,
    state.hasSocialLinks,
    state.isSubscriberAccount,
    colors.PRIMARY,
    pointsImage,
    pointsName,
    isTagMango,
    navigate,
    userId,
    showAboutContent,
  ]);

  return (
    <>
      {ProfileDetailsComponent}

      <BottomSheet open={isBottomSheetVisible} onDismiss={hideBottomSheet}>
        {actionPermissions?.canViewMangoesPurchased ? (
          <BottomSheetTile
            title={`${mangoesTitle.toTitleCase()} Purchased`}
            icon={<IoPricetags size={20} color={colors.TEXT2} />}
            onClick={() => {
              hideBottomSheet();
              navigate(ROUTES.MANGOES_PURCHASED, { userId });
            }}
          />
        ) : null}

        {actionPermissions?.canBlockOrUnblock ? (
          <>
            <hr className="dropdownDivider" />
            <BottomSheetTile
              title={state.isBlocked ? 'Unblock User' : 'Block User'}
              description={
                state.isBlocked
                  ? 'Allow this user to post and comment on your feed'
                  : 'Prevent this user from posting and commenting on your feed'
              }
              icon={
                state.isBlocked ? (
                  <IoCheckmark size={20} color={colors.GREEN} />
                ) : (
                  <IoBan size={20} color={colors.DANGER} />
                )
              }
              onClick={() => {
                hideBottomSheet();
                showConfirmationDialog();
              }}
              color={state.isBlocked ? colors.GREEN : colors.DANGER}
            />
          </>
        ) : null}
        {actionPermissions?.canRequestForService ? (
          <>
            <hr className="dropdownDivider" />
            <BottomSheetTile
              title="Request for Service"
              icon={<IoPricetags size={20} color={colors.TEXT2} />}
              onClick={() => {
                hideBottomSheet();
                if (userId) {
                  dispatch(
                    setServiceRequestModalState({
                      isOpen: true,
                      requestedTo: {
                        _id: userId,
                        name: profileDetails.userProfile.name,
                        profilePicUrl: profileDetails.userProfile.profilePicUrl,
                      },
                    }),
                  );
                }
                navigate(ROUTES.TQ_CONNECT_SERVICE_REQUESTS_CREATE);
              }}
              color={colors.TEXT2}
            />
          </>
        ) : null}
      </BottomSheet>

      <ConfirmationModal
        open={isDialogVisible}
        loading={updating}
        title={state.isBlocked ? 'Unblock User' : 'Block User'}
        message={
          state.isBlocked
            ? 'Are you sure you want to unblock this user?'
            : 'Are you sure you want to block this user?'
        }
        okayButtonColor={state.isBlocked ? colors.GREEN : colors.DANGER}
        okayButtonText={state.isBlocked ? 'Unblock User' : 'Block User'}
        cancelButtonText="Cancel"
        handleOk={handleBlockSubscriber}
        handleCancel={hideConfirmationDialog}
      />
    </>
  );
};

export default memo(UserProfileCard);
